import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fromJS } from "immutable";
import { SortingState } from "@tanstack/react-table";
import { ArrowDown, ArrowUp } from "lucide-react";
import { search } from "../../api/ProjectApi";
import ExportIcon from "../../icons/ExportIcon";
import { ContentTypes } from "../../constants/action-types";
import InputInlineWidget from "../components/InputInlineWidget";
import TableCard from "../components/TableCard";
import CellRenderer from "../../grid-v2/CellRenderer";

const GridWidget = ({
  projectId,
  widget,
  className,
  onClickUp,
  onClickDown,
  onClickExport,
  onClickSetting,
  onClickDelete,
  onUpdateTitle,
  onUpdateSubtitle,
}) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [dataGrid] = useState<{
    filters: any;
    sorting: SortingState;
    page: number;
    pageSize: number;
  }>({
    filters: widget.data?.filters || {},
    sorting: [],
    page: 0,
    pageSize: widget.data?.pageSize || 5,
  });
  const [rows, setRows] = useState<any[]>([]);

  const title = widget.data?.title;
  const subtitle = widget.data?.subtitle;
  const selectedColumns: string[] = widget.data?.selectedColumns || [];
  const titleToField: Record<string, string> = {
    NAME: "name",
    ACCOUNTS: "account",
    "LEAD SOURCE": "source",
    "ESTIMATED REV.": "estimatedRevenue",
    "CREATED DATE": "start",
    ASSIGNEE: "assignees",
    ASSIGNEES: "assignees",
    STATUS: "status",
  };
  const computedFields =
    selectedColumns.length > 0
      ? selectedColumns
          .map((t) => titleToField[t] || t.toLowerCase().replace(/\s+/g, ""))
          .filter(Boolean)
      : (widget.data?.gridFields || "name,assignees,start").split(",");
  const gridFields = computedFields.join(",");
  const gridFieldWidths = widget.data?.gridFieldWidths || "name,assignees,start";
  const widths = gridFieldWidths.split(",");
  const headers = gridFields.split(",").map((field, index) => {
    const fieldParts = field.split("_");
    return {
      selector: fieldParts[0],
      key: fieldParts[0],
      title: fieldParts[0],
      format: fieldParts.length == 2 ? fieldParts[1] : "",
      width: widths.length >= index ? widths[index] : 100,
    };
  });
  const gridType = widget.data?.gridType || ContentTypes.SALE;

  const handleSearchItems = () => {
    const searchRequest = {
      projectId,
      view: "GRID",
      types: [gridType],
      filters: dataGrid.filters,
      page: dataGrid.page,
      pageSize: dataGrid.pageSize,
    };
    setLoading(true);
    search(searchRequest).then(({ pageResult = {} }) => {
      const datas = (pageResult.content || []).map((item) => {
        const data = {};
        headers.forEach((header) => {
          data[header.key] = <CellRenderer column={fromJS(header)} row={item} />;
        });
        return data;
      });
      setRows(datas);
      setLoading(false);
    });
  };

  useEffect(() => {
    handleSearchItems();
  }, [dataGrid]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <TableCard
      title={
        <InputInlineWidget
          placeholder={t("e.g Deal Performance")}
          value={title}
          className="text-sm sm:text-sm/6 md:text-base font-medium text-gray-600 placeholder:text-gray-400"
          onFinish={onUpdateTitle}
        />
      }
      subtitle={
        subtitle ? (
          <InputInlineWidget
            placeholder={t("e.g Last year")}
            value={subtitle}
            className="text-sm text-gray-600 placeholder:text-gray-400"
            onFinish={onUpdateSubtitle}
          />
        ) : null
      }
      className={className}
      isLoading={loading}
      actions={[
        {
          label: t("Up"),
          content: <ArrowUp className="w-4 h-4 text-gray-600" />,
          onClick: (event) => onClickUp(event),
        },
        {
          label: t("Down"),
          content: <ArrowDown className="w-4 h-4 text-gray-600" />,
          onClick: (event) => onClickDown(event),
        },
        {
          label: t("Export"),
          content: <ExportIcon />,
          onClick: () => onClickExport(dataGrid.filters),
        },
      ]}
      onSetting={onClickSetting}
      onDelete={onClickDelete}
      headers={headers}
      rows={rows}
    />
  );
};

export default GridWidget;
